import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Boxes, Check, ChevronLeft, ClipboardCheck, FileUp, Loader2, QrCode, RefreshCw, ScanLine, WifiOff,
} from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import { useSubpantalla } from '../ui/navegacionReact';
import CapturaEnSerie from './CapturaEnSerie';
import RevisionFilas from './RevisionFilas';
import {
  FORMAS, PASOS_MIGRACION, UBICACION_INICIAL, contar, pasoDe, seCapturaCon, textoPaso,
} from './asistente';
import { contarPendientesCaptura, llaveCola, reenviar } from './colaCaptura';
import { SIN_RED_MOTIVO, avisoLeidas } from './migracion';

/* =====================================================================
   «Pasar tu inventario a TEGO» · U-04 fase a2 (DISEÑO-U-04 §6.1)
   =====================================================================
   Seis pasos: cómo lo llevas, dónde está, cargar, revisar y guardar,
   contar para comprobar (opcional) y acta y etiquetas.

   EL PASO LO DICE EL SERVIDOR. `pasoDe` lo calcula de la migración y del
   `siguiente` del resumen (§5.3, prueba 45); la pantalla no guarda un
   contador. Por eso, tras cualquier acción, se vuelve a leer el resumen.

   LA CAPTURA Y LA REVISIÓN SE ABREN ENCIMA, como subpantalla: capturar en
   serie cambia el `siguiente` en cuanto entra la primera captura (SUBIR →
   REVISAR), y la persona no puede ver cómo la pantalla le cambia debajo
   mientras va por el pasillo. Se vuelve con «Volver a los pasos» o con el
   botón atrás del teléfono (la misma pila).

   EL BOTÓN PRIMARIO DE CADA PASO VA ABAJO Y PEGADO (`sticky bottom-20`),
   igual que en la captura: en el flujo, por encima de los dos flotantes de
   la casa, a todo lo ancho.
   ===================================================================== */

const BOTON = 'min-h-11 rounded-xl px-3 text-sm font-semibold disabled:opacity-50';
const SECUNDARIO = `${BOTON} w-full border border-gray-300 bg-white text-gray-800`;
const PRIMARIO = 'min-h-12 w-full rounded-xl bg-[var(--color-marca)] px-3 font-semibold text-white disabled:opacity-50';

function BarraPasos({ paso, compacta }) {
  const actual = PASOS_MIGRACION.find((p) => p.n === paso) || PASOS_MIGRACION[0];
  return (
    <nav aria-label="Progreso de la migración" data-barra-pasos="" data-paso-actual={paso}
         className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-sm font-semibold text-gray-900">
        {`${textoPaso(paso)} · ${actual.nombre}${actual.opcional ? ' (opcional)' : ''}`}
      </p>
      <div className="mt-2 grid grid-cols-6 gap-1" aria-hidden="true">
        {PASOS_MIGRACION.map((p) => (
          <div key={p.n} className={`h-2 rounded-full ${p.n <= paso ? 'bg-[var(--color-marca)]' : 'bg-gray-200'}`} />
        ))}
      </div>
      {!compacta && (
        <ol className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3 md:grid-cols-6">
          {PASOS_MIGRACION.map((p) => (
            <li key={p.n} className={`flex items-start gap-1 break-words ${p.n === paso ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
              {p.n < paso && <Check className="mt-0.5 h-3 w-3 shrink-0 text-green-700" aria-hidden="true" />}
              <span className="min-w-0">{p.n}. {p.nombre}{p.opcional ? ' (opcional)' : ''}</span>
            </li>
          ))}
        </ol>
      )}
    </nav>
  );
}

/** El primario del paso, pegado abajo, con su motivo si está apagado. */
function BarraPrimaria({ children, onClick, motivo = '', ocupado = false }) {
  return (
    <div className="sticky bottom-20 z-30 flex flex-col gap-1 rounded-xl bg-white/95 p-1 shadow-md" data-barra-fija="">
      <button type="button" className={PRIMARIO} disabled={Boolean(motivo) || ocupado} onClick={onClick}>
        {children}
      </button>
      {motivo && <p className="px-1 text-xs text-gray-700" data-motivo="primario">{motivo}</p>}
    </div>
  );
}

export default function MigracionAsistente({ irA, visibles }) {
  const avisos = useAvisos();
  const [migracion, setMigracion] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [saltarConteo, setSaltarConteo] = useState(false);
  const [ubicaciones, setUbicaciones] = useState(null);
  const [elegida, setElegida] = useState('');
  const [enLinea, setEnLinea] = useState(typeof navigator === 'undefined' || navigator.onLine !== false);
  const entrada = useRef(null);

  useEffect(() => {
    const on = () => setEnLinea(true);
    const off = () => setEnLinea(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const cargar = useCallback(async () => {
    setError('');
    try {
      const actual = await api.get('/migracion/actual');
      const m = actual?.migracion || null;
      const r = m ? await api.get(`/migracion/${m.id}/resumen`) : null;
      setMigracion(m);
      setResumen(r);
    } catch (e) {
      setError(e.message || 'El servidor no dijo por qué.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // LAS CAPTURAS QUE ESPERAN EN EL TELÉFONO SE ENVÍAN TAMBIÉN DESDE AQUÍ, no
  // solo con la captura abierta: quien vuelve a los pasos sin señal y la
  // recupera aquí no tiene que reabrir la captura para que salgan.
  const { usuario } = useAuth();
  const llave = llaveCola(usuario, migracion?.id);
  const [enTelefono, setEnTelefono] = useState(0);
  const enviarDelTelefono = useCallback(async () => {
    if (!llave || !migracion?.id) return;
    setEnTelefono(contarPendientesCaptura(llave));
    if (abierta === 'captura' || contarPendientesCaptura(llave) === 0) return;
    const r = await reenviar(llave, migracion.id);
    setEnTelefono(r.restantes);
    if (r.enviados) {
      avisos.exito(r.enviados === 1 ? 'Se envió 1 captura que esperaba en este teléfono.'
        : `Se enviaron ${r.enviados} capturas que esperaban en este teléfono.`);
      cargar();
    }
    if (r.rechazadas.length) avisos.aviso(`${r.rechazadas[0].motivo} Revísala en la captura rápida.`);
  }, [llave, migracion?.id, abierta, avisos, cargar]);
  useEffect(() => { enviarDelTelefono(); }, [enviarDelTelefono]);
  useEffect(() => {
    window.addEventListener('online', enviarDelTelefono);
    return () => window.removeEventListener('online', enviarDelTelefono);
  }, [enviarDelTelefono]);

  const paso = pasoDe(migracion, resumen, { saltarConteo });

  // Atrás en el teléfono cierra la captura o la revisión y vuelve a los pasos.
  const cerrarSub = useCallback(() => { setAbierta(null); cargar(); }, [cargar]);
  useSubpantalla('migracion-paso', abierta !== null, cerrarSub, { valor: abierta || true });

  useEffect(() => {
    if (paso !== 2 || ubicaciones !== null) return;
    api.get('/ubicaciones')
      .then((u) => {
        const lista = Array.isArray(u) ? u : [];
        setUbicaciones(lista);
        setElegida((antes) => antes || (lista.length === 1 ? lista[0].codigo : ''));
      })
      .catch((e) => { setUbicaciones([]); avisos.error(e.message); });
  }, [paso, ubicaciones, avisos]);

  const puedeIr = (id) => typeof irA === 'function' && (!Array.isArray(visibles) || visibles.includes(id));
  const motivoRed = enLinea ? '' : SIN_RED_MOTIVO;

  async function hacer(accion) {
    setOcupado(true);
    try {
      await accion();
    } catch (e) {
      avisos.error(e.message);
    } finally {
      setOcupado(false);
    }
  }

  const elegirForma = (forma) => hacer(async () => {
    let m = migracion ? await api.patch(`/migracion/${migracion.id}`, { forma_actual: forma })
      : await api.post('/migracion', { forma_actual: forma });
    // «Empezar» es idempotente: si ya había una abierta con otra respuesta, se cambia.
    if (m.forma_actual !== forma) m = await api.patch(`/migracion/${m.id}`, { forma_actual: forma });
    setMigracion(m);
    if (seCapturaCon(forma) && m.ubicacion_omision) setAbierta('captura');
    await cargar();
  });

  const fijarUbicacion = (codigo) => hacer(async () => {
    const m = await api.patch(`/migracion/${migracion.id}`, { ubicacion_omision: codigo });
    setMigracion(m);
    avisos.exito(`Lo que no diga ubicación irá a ${m.ubicacion_omision?.nombre || codigo}.`);
    if (seCapturaCon(m.forma_actual)) setAbierta('captura');
    await cargar();
  });

  const crearAlmacen = () => hacer(async () => {
    const u = await api.post('/ubicaciones', { ...UBICACION_INICIAL, tipo: 'ALMACEN' });
    const codigo = u?.codigo || UBICACION_INICIAL.codigo;
    setUbicaciones(null);
    const m = await api.patch(`/migracion/${migracion.id}`, { ubicacion_omision: codigo });
    setMigracion(m);
    avisos.exito(`Se creó «${UBICACION_INICIAL.nombre}» y lo que cargues irá ahí.`);
    if (seCapturaCon(m.forma_actual)) setAbierta('captura');
    await cargar();
  });

  const subir = (archivo) => hacer(async () => {
    if (!archivo) return;
    try {
      const r = await api.subir(`/migracion/${migracion.id}/lotes`, archivo);
      avisos.exito(avisoLeidas(r.totales.filas, r.lote.nombre_archivo));
      await cargar();
      setAbierta('revision');
    } finally {
      if (entrada.current) entrada.current.value = '';
    }
  });

  const reintentar = () => { setCargando(true); cargar(); };
  const porEstado = resumen?.por_estado || {};
  const forma = migracion?.forma_actual;

  function pintarPaso1() {
    return (
      <section className="flex flex-col gap-3" data-paso="1">
        <h2 className="text-lg font-semibold text-gray-900">¿Cómo llevas hoy tu inventario?</h2>
        <Ayuda id="migracion.forma_actual" />
        {FORMAS.map((f) => (
          <button key={f.forma} type="button" data-forma={f.forma} aria-pressed={forma === f.forma}
                  disabled={ocupado || !enLinea} onClick={() => elegirForma(f.forma)}
                  className={`min-h-20 w-full rounded-2xl border-2 bg-white p-4 text-left disabled:opacity-50 ${
                    forma === f.forma ? 'border-[var(--color-marca)]' : 'border-gray-200'}`}>
            <span className="block text-base font-semibold text-gray-900">{f.titulo}</span>
            <span className="mt-1 block text-sm text-gray-600">{f.despues}</span>
          </button>
        ))}
        {motivoRed && <p className="text-xs text-gray-700">{motivoRed}</p>}
      </section>
    );
  }

  function pintarPaso2() {
    const motivo = motivoRed || (elegida ? '' : 'Elige una ubicación de la lista.');
    return (
      <section className="flex flex-col gap-3" data-paso="2">
        <h2 className="text-lg font-semibold text-gray-900">¿Dónde está la mercancía?</h2>
        {ubicaciones === null && (
          <p className="flex items-center gap-2 text-sm text-gray-600" aria-busy="true">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Leyendo tus ubicaciones…
          </p>
        )}
        {ubicaciones !== null && ubicaciones.length > 0 && (
          <>
            <label className="flex flex-col gap-1 text-sm text-gray-800">
              Ubicación
              <select className="entrada" value={elegida} disabled={ocupado} onChange={(e) => setElegida(e.target.value)}>
                {ubicaciones.length > 1 && <option value="">Elige una ubicación</option>}
                {ubicaciones.map((u) => <option key={u.id} value={u.codigo}>{u.nombre} ({u.codigo})</option>)}
              </select>
              <Ayuda id="migracion.ubicacion_omision" />
            </label>
            <BarraPrimaria motivo={motivo} ocupado={ocupado} onClick={() => fijarUbicacion(elegida)}>Seguir</BarraPrimaria>
          </>
        )}
        {ubicaciones !== null && ubicaciones.length === 0 && (
          <>
            <p className="text-sm text-gray-700">
              Tu empresa no tiene ubicaciones dadas de alta. Crea una para empezar; después puedes añadir pasillos y estantes.
            </p>
            <BarraPrimaria motivo={motivoRed} ocupado={ocupado} onClick={crearAlmacen}>
              Crear «{UBICACION_INICIAL.nombre}»
            </BarraPrimaria>
          </>
        )}
      </section>
    );
  }

  function pintarPaso3() {
    const archivo = (
      <div className="flex flex-col gap-1">
        <label className={`${SECUNDARIO} flex cursor-pointer items-center justify-center`}>
          <FileUp className="mr-1 h-4 w-4" aria-hidden="true" />{seCapturaCon(forma) ? 'Subir un archivo de Excel o CSV' : 'Subir archivo'}
          <input ref={entrada} type="file" accept=".xlsx,.csv" className="entrada sr-only"
                 disabled={ocupado || !enLinea} onChange={(e) => subir(e.target.files?.[0])} />
        </label>
        <Ayuda id="migracion.archivo" />
      </div>
    );
    if (seCapturaCon(forma)) {
      return (
        <section className="flex flex-col gap-3" data-paso="3">
          <h2 className="text-lg font-semibold text-gray-900">Captura lo que tienes</h2>
          <p className="text-sm text-gray-700">
            Con el teléfono en la mano: escanea o escribe cada producto y cuántas hay. Todo queda para revisar antes de
            entrar al inventario.
          </p>
          {archivo}
          <BarraPrimaria onClick={() => setAbierta('captura')}>
            <ScanLine className="mr-1 inline h-5 w-5" aria-hidden="true" />Empezar a capturar
          </BarraPrimaria>
        </section>
      );
    }
    return (
      <section className="flex flex-col gap-3" data-paso="3">
        <h2 className="text-lg font-semibold text-gray-900">Sube tu archivo</h2>
        <p className="text-sm text-gray-700">
          Basta una columna con el nombre del producto y otra con cuántas hay. Excel (.xlsx) o CSV.
        </p>
        {archivo}
        <button type="button" className={SECUNDARIO} onClick={() => setAbierta('captura')}>
          <ScanLine className="mr-1 inline h-4 w-4" aria-hidden="true" />Capturar desde el teléfono
        </button>
      </section>
    );
  }

  function pintarPaso4() {
    const pend = Number(porEstado.PENDIENTE || 0);
    const aprob = Number(porEstado.APROBADA || 0);
    return (
      <section className="flex flex-col gap-3" data-paso="4">
        <h2 className="text-lg font-semibold text-gray-900">Revisa y guarda</h2>
        <ul className="flex flex-col gap-1 text-sm text-gray-800">
          <li>Por decidir: {contar(pend, 'fila', 'filas')}</li>
          <li>Aprobadas sin guardar: {contar(aprob, 'fila', 'filas')}</li>
          <li>Ya guardadas: {contar(Number(resumen?.aplicadas || 0), 'fila', 'filas')}</li>
        </ul>
        <button type="button" className={SECUNDARIO} onClick={() => setAbierta('captura')}>
          <ScanLine className="mr-1 inline h-4 w-4" aria-hidden="true" />Capturar más
        </button>
        <BarraPrimaria onClick={() => setAbierta('revision')}>Revisar y guardar</BarraPrimaria>
      </section>
    );
  }

  function pintarPaso5() {
    const n = Number(resumen?.ubicaciones_por_contar || 0);
    const abiertos = Number(resumen?.conteos?.abiertos || 0);
    return (
      <section className="flex flex-col gap-3" data-paso="5">
        <h2 className="text-lg font-semibold text-gray-900">Contar para comprobar (opcional)</h2>
        <p className="text-sm text-gray-700">
          Cuenta cada ubicación sin ver lo cargado: TEGO compara y marca lo que no cuadra.
        </p>
        {n > 0 && <p className="text-sm text-gray-800">{contar(n, 'ubicación', 'ubicaciones')} con lo guardado sin contar.</p>}
        {abiertos > 0 && <p className="text-sm text-gray-800">{contar(abiertos, 'conteo abierto', 'conteos abiertos')}.</p>}
        <button type="button" className={SECUNDARIO} onClick={() => setSaltarConteo(true)}>Saltar este paso</button>
        {puedeIr('conteos') && (
          <BarraPrimaria onClick={() => irA('conteos')}>
            <ClipboardCheck className="mr-1 inline h-5 w-5" aria-hidden="true" />Ir a Conteos
          </BarraPrimaria>
        )}
      </section>
    );
  }

  function pintarPaso6() {
    const sinCodigo = Number(resumen?.productos_sin_codigo || 0);
    return (
      <section className="flex flex-col gap-3" data-paso="6">
        <h2 className="text-lg font-semibold text-gray-900">Tu inventario ya está en TEGO</h2>
        <p className="text-sm text-gray-700">
          {contar(Number(resumen?.aplicadas || 0), 'fila guardada', 'filas guardadas')} en el inventario.
        </p>
        {resumen?.acta && <p className="text-sm text-gray-700">Acta con folio {resumen.acta.folio}.</p>}
        {sinCodigo > 0 && puedeIr('etiquetas') && (
          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-3">
            <p className="text-sm text-gray-800">
              {contar(sinCodigo, 'producto', 'productos')} de esta migración no traían código. En Etiquetas QR
              imprimes la suya para pegarla.
            </p>
            <button type="button" className={SECUNDARIO} onClick={() => irA('etiquetas')}>
              <QrCode className="mr-1 inline h-4 w-4" aria-hidden="true" />Imprimir etiquetas
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button type="button" className={SECUNDARIO} onClick={() => setAbierta('captura')}>
            <ScanLine className="mr-1 inline h-4 w-4" aria-hidden="true" />Capturar más
          </button>
          <button type="button" className={SECUNDARIO} onClick={() => setAbierta('revision')}>
            <FileUp className="mr-1 inline h-4 w-4" aria-hidden="true" />Subir otro archivo
          </button>
        </div>
        {puedeIr('existencias') && (
          <BarraPrimaria onClick={() => irA('existencias')}>
            <Boxes className="mr-1 inline h-5 w-5" aria-hidden="true" />Ver mis existencias
          </BarraPrimaria>
        )}
      </section>
    );
  }

  const pintarPaso = { 1: pintarPaso1, 2: pintarPaso2, 3: pintarPaso3, 4: pintarPaso4, 5: pintarPaso5, 6: pintarPaso6 }[paso];

  return (
    <div className="flex flex-col gap-4" data-migracion="asistente">
      {!abierta && (
        <>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Pasar tu inventario a TEGO</h1>
            <p className="text-sm text-gray-600">Cargas lo que tienes, lo revisas fila por fila y guardas solo lo que apruebas.</p>
          </div>
          <Ayuda id="migracion.pantalla" />
          {!enLinea && (
            <p className="flex items-start gap-2 rounded-xl border border-yellow-300 bg-yellow-100 p-3 text-sm text-yellow-900"
               data-banda="sin-red">
              <WifiOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Sin conexión. Lo que ya cargaste está guardado.</span>
            </p>
          )}
          {enTelefono > 0 && (
            <p className="rounded-xl border border-yellow-300 bg-yellow-100 p-3 text-sm text-yellow-900" data-en-telefono={enTelefono}>
              {enTelefono === 1 ? '1 captura espera en este teléfono; se envía sola con señal.'
                : `${enTelefono} capturas esperan en este teléfono; se envían solas con señal.`}
            </p>
          )}
        </>
      )}

      {cargando && (
        <div className="flex flex-col gap-2" aria-busy="true">
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />Leyendo cómo va tu migración…
          </p>
          <div className="h-16 rounded-xl bg-gray-100" />
          <div className="h-24 rounded-xl bg-gray-100" />
        </div>
      )}

      {!cargando && error && (
        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-800">
          <p className="break-words">No se pudo leer cómo va tu migración. {error}</p>
          <button type="button" className={`${SECUNDARIO} mt-2`} onClick={reintentar}>
            <RefreshCw className="mr-1 inline h-4 w-4" aria-hidden="true" />Volver a intentarlo
          </button>
        </div>
      )}

      {!cargando && !error && (
        <>
          <BarraPasos paso={paso} compacta={Boolean(abierta)} />
          {abierta ? (
            <div className="flex flex-col gap-3">
              <button type="button" className={`${BOTON} w-fit border border-gray-300 bg-white text-gray-800`}
                      onClick={() => { setAbierta(null); cargar(); }}>
                <ChevronLeft className="mr-1 inline h-4 w-4" aria-hidden="true" />Volver a los pasos
              </button>
              {abierta === 'captura' && migracion && <CapturaEnSerie migracion={migracion} />}
              {abierta === 'revision' && <RevisionFilas onCapturar={() => setAbierta('captura')} />}
            </div>
          ) : (
            pintarPaso?.()
          )}
        </>
      )}
    </div>
  );
}
